'use client';

import { ArrowLeft, BarChart, Check, CheckCircle2, Clock, Code2, Copy, Edit, ExternalLink, Hash, XCircle } from 'lucide-react';
import { Deployment, Project } from '../../../types';
import React, { useRef, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useOrganization, useUser } from '@clerk/clerk-react';

import { Button } from '../ui/button';
import StatusPill from './StatusPill';

interface ProjectDetailsProps {
    project: Project;
    onClose: () => void;
}

export default function ProjectDetails({ project, onClose }: ProjectDetailsProps) {
    const [showEmbedDropdown, setShowEmbedDropdown] = useState(false);
    const [copied, setCopied] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [imageError, setImageError] = useState(false);
    const { user } = useUser();
    const { organization } = useOrganization();
    const navigate = useNavigate();
    const params = useParams({ from: '/org/$orgSlug/project-details/$projectId' });

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true
        });
    };

    const getDeploymentUrl = (deployment: Deployment) => {
        // @ts-expect-error - handling custom_domain which might not be in the type
        return deployment.finalUrl || (deployment.custom_domain ? `https://${deployment.custom_domain}` : deployment.service_url);
    };

    const generateEmbedCode = (deployment: Deployment) => {
        const url = getDeploymentUrl(deployment);
        return `<iframe src="${url}" width="100%" height="600" style="border:none;border-radius:4px;" title="${project.name}"></iframe>`;
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    // Close dropdown when clicking outside
    React.useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowEmbedDropdown(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <div className="w-full py-8">
            {/* Header with Back Button */}
            <div className="flex items-center justify-between">
                <Button
                    onClick={onClose}
                    variant="ghost"
                    size="sm"
                    className="flex items-center gap-2 h-8 text-sm text-gray-500 hover:bg-gray-200 hover:text-gray-900"
                >
                    <ArrowLeft className="h-4 w-4" />
                </Button>

                {/* Visit & Repository Buttons */}
                <div className="flex items-center">
                    {project.isPublished !== false && (
                        <>
                            <button
                                onClick={() => {
                                    // Navigate to editor page using TanStack Router
                                    navigate({
                                        to: '/org/$orgSlug/$projectId',
                                        params: {
                                            orgSlug: params.orgSlug,
                                            projectId: project.id
                                        }
                                    });
                                }}
                                className="px-2 py-1 text-sm rounded-md border bg-transparent border-gray-200 hover:bg-gray-50 transition-all mr-2"
                            >
                                <Edit className="w-4 h-4 inline-block mr-1" />
                                Edit
                            </button>
                            <button
                                disabled={true}
                                className="px-2 py-1 text-sm rounded-md border bg-transparent border-gray-200 hover:bg-gray-50 transition-all mr-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Contact founders@structuredlabs.com to set up"
                            >
                                <BarChart className="w-4 h-4 inline-block mr-1" />
                                Analytics
                            </button>
                            <div className="relative mr-2" ref={dropdownRef}>
                                <button
                                    onClick={() => setShowEmbedDropdown(!showEmbedDropdown)}
                                    className={`px-2 py-1 text-sm rounded-md border transition-all ${showEmbedDropdown
                                        ? 'bg-gray-100 border-gray-300 font-medium'
                                        : 'bg-transparent border-gray-200 hover:bg-gray-50'
                                        }`}
                                >
                                    <Code2 className="w-4 h-4 inline-block mr-1" />
                                    Embed
                                </button>

                                {showEmbedDropdown && project.deployments && project.deployments[0] && (
                                    <div className="absolute right-0 mt-2 w-96 bg-popover border border-gray-200 rounded-md shadow-md z-10 p-4">
                                        <h3 className="text-sm font-medium text-popover-foreground mb-2">Embed this project</h3>
                                        <div className="bg-gray-50 p-3 rounded-md mb-3 text-xs font-mono overflow-x-auto text-muted-foreground">
                                            {project.deployments && generateEmbedCode(project.deployments[0])}
                                        </div>
                                        <button
                                            onClick={() => project.deployments && copyToClipboard(generateEmbedCode(project.deployments[0]))}
                                            className="px-2 py-1 text-sm bg-gray-800 text-white rounded-md font-medium hover:bg-gray-700 transition-colors"
                                        >
                                            {copied ? (
                                                <>
                                                    <Check className="w-3 h-3 inline-block mr-1" />
                                                    Copied!
                                                </>
                                            ) : (
                                                <>
                                                    <Copy className="w-3 h-3 inline-block mr-1" />
                                                    Copy Code
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={() => {
                                    if (project.deployments && project.deployments[0]) {
                                        window.open(getDeploymentUrl(project.deployments[0]), "_blank");
                                    }
                                }}
                                className="px-2 py-1 text-sm bg-gray-800 text-white rounded-md font-medium hover:bg-gray-700 transition-colors"
                            >
                                <ExternalLink className="w-4 h-4 inline-block mr-1" />
                                Visit
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Main Content Area */}
            <div className="mt-8 flex gap-8">
                {/* App Preview */}
                <div className="w-1/3">
                    <div className="w-full bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
                        {/* Browser-like header */}
                        <div className="h-7 bg-gray-50 border-b border-gray-200 flex items-center px-4">
                            <div className="flex space-x-2">
                                <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                                <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                                <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                            </div>
                        </div>
                        {/* Preview image */}
                        <div className="p-4">
                            {project.deployments && project.deployments[0] ? (
                                imageError ? (
                                    <div className="w-full aspect-video bg-muted rounded-md flex flex-col items-center justify-center gap-2 text-center p-4">
                                        <div className="w-12 h-12 rounded-full bg-muted-foreground/10 flex items-center justify-center">
                                            <img
                                                src="/symbol.png"
                                                alt="Preview"
                                                className="w-6 h-6 opacity-50"
                                            />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-foreground">Preview unavailable</p>
                                            <p className="text-xs text-muted-foreground">Unable to load preview for this deployment</p>
                                        </div>
                                    </div>
                                ) : (
                                    <img
                                        src={'/app.png'}
                                        alt={`${project.name} preview`}
                                        className="w-full rounded-md"
                                        onError={() => setImageError(true)}
                                    />
                                )
                            ) : (
                                <div className="w-full aspect-video bg-muted rounded-md flex flex-col items-center justify-center gap-2 text-center p-4">
                                    <div className="w-12 h-12 rounded-full bg-muted-foreground/10 flex items-center justify-center">
                                        <img
                                            src="/symbol.png"
                                            alt="Preview"
                                            className="w-6 h-6 opacity-50"
                                        />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-foreground">No preview available</p>
                                        <p className="text-xs text-muted-foreground">Deploy your app to see a preview</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Project Details and Status */}
                <div className="w-2/3">
                    {/* Project Header */}
                    <div className="flex items-center mb-4">
                        <img src={"/symbol.png"} className="h-8 w-8 rounded-full mr-4 opacity-50" alt={project.name} />
                        <div>
                            <div className="flex items-center gap-2">
                                <h2 className="text-xl font-semibold text-foreground">{project.name}</h2>
                                <StatusPill isPublished={project.isPublished} deployment={project.deployments?.[0]} />
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                                By <span className="font-medium text-foreground">{project.ownerName}</span>
                            </p>
                        </div>
                    </div>

                    {/* Project Information */}
                    <div className="space-y-4">
                        {/* Latest Deployment */}
                        {project.deployments && project.deployments.length > 0 && (
                            <div className="bg-muted rounded-lg p-2">
                                <div className="space-y-4">
                                    {/* Deployment Status and Timing */}
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-foreground">Created</span>
                                            <span className="text-muted-foreground">{formatDate(project.deployments[0].createdAt)}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-foreground">Updated</span>
                                            <span className="text-muted-foreground">{formatDate(project.deployments[0].updatedAt)}</span>
                                        </div>
                                        {project.deployments[0].service_url && (
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-foreground">Service URL</span>
                                                <span className="text-muted-foreground font-mono text-xs">{project.deployments[0].service_url}</span>
                                            </div>
                                        )}
                                        {project.deployments[0].custom_domain && (
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-foreground">Custom Domain</span>
                                                <span className="text-muted-foreground font-mono text-xs">{project.deployments[0].custom_domain}</span>
                                            </div>
                                        )}
                                        {project.deployments[0].build_url && (
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-foreground">Build URL</span>
                                                <a
                                                    href={project.deployments[0].build_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-primary hover:underline font-mono text-xs"
                                                >
                                                    {project.deployments[0].build_url}
                                                </a>
                                            </div>
                                        )}
                                        {project.deployments[0].message && (
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-foreground">Message</span>
                                                <span className="text-muted-foreground text-xs">{project.deployments[0].message}</span>
                                            </div>
                                        )}
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-foreground">Project Slug</span>
                                            <span className="text-muted-foreground font-mono text-xs">{project.deployments[0].projectSlug}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-foreground">Deployment ID</span>
                                            <span className="text-muted-foreground font-mono text-xs">{project.deployments[0].deployment_id}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-foreground">User</span>
                                            <div className="flex items-center gap-2">
                                                {user?.imageUrl && (
                                                    <img
                                                        src={user.imageUrl}
                                                        alt="User avatar"
                                                        className="w-5 h-5 rounded-full"
                                                    />
                                                )}
                                                <span className="text-muted-foreground text-xs">ID: {project.deployments[0].userId}</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-foreground">Organization</span>
                                            <div className="flex items-center gap-2">
                                                {organization?.imageUrl ? (
                                                    <>
                                                        <img
                                                            src={organization.imageUrl}
                                                            alt="Organization logo"
                                                            className="w-5 h-5 rounded-full"
                                                        />
                                                        <span className="text-muted-foreground text-xs">ID: {project.deployments[0].orgId}</span>
                                                    </>
                                                ) : (
                                                    <span className="text-muted-foreground">None</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-foreground">Active</span>
                                            <span className={`text-muted-foreground ${project.deployments[0].isActive ? 'text-green-500' : 'text-red-500'}`}>{project.deployments[0].isActive ? 'Yes' : 'No'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Versions Section */}
            {project.deployments && project.deployments.length > 0 && (
                <div className="mt-8">
                    <h3 className="text-base font-semibold text-foreground mb-4">Versions</h3>
                    <div className="space-y-4">
                        {project.deployments.map((deployment) => {
                            const isActive = deployment.isActive;
                            const statusColor = isActive ? 'text-green-500' : 'text-red-500';
                            const statusIcon = isActive ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />;

                            return (
                                <div key={deployment.deployment_id} className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <div className={`p-1 rounded-full ${isActive ? 'bg-green-100' : 'bg-red-100'}`}>
                                                {statusIcon}
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-foreground">{deployment.message || 'Version'}</p>
                                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                    <Hash className="w-3 h-3" />
                                                    <span>{deployment.projectSlug}</span>
                                                    <span>•</span>
                                                    <Clock className="w-3 h-3" />
                                                    <span>{formatDate(deployment.timestamp)}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {deployment.build_url && (
                                                <a
                                                    href={deployment.build_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary hover:text-primary/80 hover:bg-primary/5 rounded-md transition-colors"
                                                >
                                                    <ExternalLink className="w-4 h-4 gray-200" />
                                                </a>
                                            )}
                                            {deployment.service_url && (
                                                <a
                                                    href={deployment.service_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary hover:text-primary/80 hover:bg-primary/5 rounded-md transition-colors"
                                                >
                                                    <ExternalLink className="w-4 h-4 gray-200" />
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 text-xs text-muted-foreground">
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-1">
                                                <span className="font-medium">Status:</span>
                                                <span className={statusColor}>{isActive ? 'Active' : 'Inactive'}</span>
                                            </div>
                                            {deployment.service_url && (
                                                <div className="flex items-center gap-1">
                                                    <span className="font-medium">Service:</span>
                                                    <a
                                                        href={deployment.service_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-primary hover:underline font-mono"
                                                    >
                                                        {deployment.service_url.replace('https://', '')}
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            {deployment.custom_domain && (
                                                <div className="flex items-center gap-1">
                                                    <span className="font-medium">Domain:</span>
                                                    <a
                                                        href={`https://${deployment.custom_domain}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-primary hover:underline font-mono"
                                                    >
                                                        {deployment.custom_domain}
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* <div className="mt-8">
                <h3 className="text-base font-semibold text-foreground mb-4">Raw Data</h3>

                <div className="mb-6">
                    <h4 className="text-sm font-medium mb-2">Project Object</h4>
                    <pre className="bg-gray-50 p-4 rounded-md overflow-auto text-xs max-h-64">
                        {JSON.stringify(project, null, 2)}
                    </pre>
                </div>

                {project.deployments && project.deployments.length > 0 && (
                    <div>
                        <h4 className="text-sm font-medium mb-2">Deployments Array</h4>
                        <pre className="bg-gray-50 p-4 rounded-md overflow-auto text-xs max-h-64">
                            {JSON.stringify(project.deployments, null, 2)}
                        </pre>
                    </div>
                )}
            </div> */}
        </div>
    );
}